import { Injectable } from '@angular/core';
import { Observable, delay, of, throwError, switchMap } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import {
  SignInRequest,
  SignUpRequest,
  ResetPasswordRequest,
  ResetPasswordConfirmRequest,
  AuthResponse,
  AuthData,
  LoginResponse,
  CurrentUserResponse,
} from '../models/auth.model';
import { User } from '../models/user.model';
import { API_ENDPOINTS } from '../constants/api.constants';
import { LoggingService } from './logging.service';

/**
 * Authentication API Service
 * Handles all authentication-related API calls following backend JWT flow
 */
@Injectable({
  providedIn: 'root',
})
export class AuthApiService {
  private readonly baseUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private logger: LoggingService
  ) {}

  /**
   * Sign in user with backend JWT authentication
   * 1. POST /auth/login with email/password -> receive JWT token
   * 2. GET /auth/current-user with JWT -> receive user data
   * 3. Return combined AuthData
   */
  signIn(request: SignInRequest): Observable<AuthData> {
    if (environment.features.enableMockData) {
      // Mock implementation for development
      const mockAuthData: AuthData = {
        user: {
          _id: '1',
          email: request.email,
          fullName: 'Test User',
          role: 'merchant',
          country: 'GE',
          createdAt: new Date(),
          updatedAt: new Date(),
          emailVerified: true,
          phoneVerified: false,
          twoFactorEnabled: false,
          preferences: {
            language: 'en',
            theme: 'light',
            currency: 'GEL',
            timezone: 'Asia/Tbilisi',
            notifications: {
              email: true,
              sms: false,
              push: true,
              transactionAlerts: true,
              securityAlerts: true,
              marketingEmails: false,
            },
          },
        },
        tokens: {
          accessToken: 'mock-access-token',
          refreshToken: 'mock-refresh-token',
          expiresIn: 0, // Tokens don't expire
          tokenType: 'Bearer',
        },
      };
      
      return of(mockAuthData).pipe(delay(1000));
    }

    // Real API implementation - Step 1: Login to get JWT
    return this.http.post<LoginResponse>(
      `${this.baseUrl}${API_ENDPOINTS.AUTH.SIGN_IN}`,
      { email: request.email, password: request.password }
    ).pipe(
      switchMap((response) => {
        if (!response.success || !response.data?.token) {
          throw new Error(response.message || 'Login failed');
        }

        const token = response.data.token;
        this.logger.info('Login successful, token received, fetching user data');

        // IMPORTANT: Store token temporarily so interceptor can use it
        sessionStorage.setItem('token', token);

        // Step 2: Fetch user data with JWT (interceptor will add the Authorization header)
        return this.http.get<CurrentUserResponse>(
          `${this.baseUrl}${API_ENDPOINTS.AUTH.CURRENT_USER}`
        ).pipe(
          map((userResponse) => {
            if (!userResponse.success || !userResponse.data) {
              throw new Error(userResponse.message || 'Failed to load user data');
            }

            // Map _id to id for compatibility
            const user = userResponse.data;
            const userWithId = user as any;
            
            this.logger.info('User data from backend:', userWithId);
            this.logger.info('User _id:', userWithId._id);
            this.logger.info('User id:', userWithId.id);
            
            // Always map _id to id if _id exists
            if (userWithId._id) {
              userWithId.id = userWithId._id;
              this.logger.info('Mapped _id to id:', userWithId.id);
            }
            
            // Ensure id exists (fallback to _id if id is missing)
            if (!userWithId.id && userWithId._id) {
              userWithId.id = userWithId._id;
            }
            
            if (!userWithId.id) {
              this.logger.error('CRITICAL: User ID is still undefined after mapping!', userWithId);
            }

            // Add default avatar if not present
            if (!user.avatar) {
              user.avatar = 'assets/images/users/avatar.svg';
            }

            // Return combined auth data
            const authData: AuthData = {
              user,
              tokens: {
                accessToken: token,
                tokenType: 'Bearer',
                expiresIn: 0, // Tokens don't expire
              },
            };

            return authData;
          })
        );
      }),
      catchError((error) => {
        this.logger.error('Sign in error', error);
        const errorMessage = error.error?.message || error.message || 'An error occurred during sign in';
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Load current user with JWT token
   * Used for refreshing user data or initializing from stored token
   * Note: Token should already be in storage for interceptor to use
   */
  loadCurrentUser(token: string): Observable<User> {
    // Ensure token is in storage for interceptor
    if (!sessionStorage.getItem('token')) {
      sessionStorage.setItem('token', token);
    }

    // Interceptor will add Authorization header automatically
    return this.http.get<CurrentUserResponse>(
      `${this.baseUrl}${API_ENDPOINTS.AUTH.CURRENT_USER}`
    ).pipe(
      map((response) => {
        if (!response.success || !response.data) {
          throw new Error(response.message || 'Failed to load user data');
        }

        // Map _id to id for compatibility
        const user = response.data;
        const userWithId = user as any;
        
        // Always map _id to id if _id exists
        if (userWithId._id) {
          userWithId.id = userWithId._id;
        }
        
        // Ensure id exists (fallback to _id if id is missing)
        if (!userWithId.id && userWithId._id) {
          userWithId.id = userWithId._id;
        }

        // Add default avatar if not present
        if (!user.avatar) {
          user.avatar = 'assets/images/users/avatar.svg';
        }

        return user;
      }),
      catchError((error) => {
        this.logger.error('Load current user error', error);
        const errorMessage = error.error?.message || error.message || 'Failed to load user';
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Sign up new user
   */
  signUp(request: SignUpRequest): Observable<AuthData> {
    if (environment.features.enableMockData) {
      // Mock implementation
      const mockAuthData: AuthData = {
        user: {
          _id: '1',
          email: request.email,
          fullName: request.fullName,
          role: 'merchant',
          country: request.country,
          createdAt: new Date(),
          updatedAt: new Date(),
          emailVerified: false,
          phoneVerified: false,
          twoFactorEnabled: false,
          preferences: {
            language: 'en',
            theme: 'light',
            currency: 'GEL',
            timezone: 'Asia/Tbilisi',
            notifications: {
              email: true,
              sms: false,
              push: true,
              transactionAlerts: true,
              securityAlerts: true,
              marketingEmails: false,
            },
          },
        },
        tokens: {
          accessToken: 'mock-access-token',
          refreshToken: 'mock-refresh-token',
          expiresIn: 0, // Tokens don't expire
          tokenType: 'Bearer',
        },
      };
      
      return of(mockAuthData).pipe(delay(1000));
    }

    // Real API implementation
    return this.http.post<AuthResponse>(
      `${this.baseUrl}${API_ENDPOINTS.AUTH.SIGN_UP}`,
      request
    ).pipe(
      switchMap((response) => {
        if (!response.success || !response.data?.token) {
          throw new Error(response.message || 'Sign up failed');
        }

        const token = response.data.token;
        
        // Store token so interceptor can use it
        sessionStorage.setItem('token', token);

        // Fetch user data after signup
        return this.loadCurrentUser(token).pipe(
          map((user) => ({
            user,
            tokens: {
              accessToken: token,
              tokenType: 'Bearer' as const,
              expiresIn: 0, // Tokens don't expire
            },
          }))
        );
      }),
      catchError((error) => {
        this.logger.error('Sign up error', error);
        const errorMessage = error.error?.message || error.message || 'An error occurred during sign up';
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Request password reset email
   */
  requestPasswordReset(request: ResetPasswordRequest): Observable<void> {
    if (environment.features.enableMockData) {
      this.logger.info('Mock: Password reset email sent', { email: request.email });
      return of(void 0).pipe(delay(1000));
    }

    return this.http.post<void>(
      `${this.baseUrl}${API_ENDPOINTS.AUTH.RESET_PASSWORD_REQUEST}`,
      request
    );
  }

  /**
   * Verify password reset token
   */
  verifyResetToken(token: string): Observable<{ valid: boolean }> {
    if (environment.features.enableMockData) {
      return of({ valid: token.length > 10 }).pipe(delay(1000));
    }

    return this.http.get<{ valid: boolean }>(
      `${this.baseUrl}${API_ENDPOINTS.AUTH.VERIFY_EMAIL}`,
      { params: { token } }
    );
  }

  /**
   * Confirm password reset with new password
   */
  confirmPasswordReset(request: ResetPasswordConfirmRequest): Observable<void> {
    if (environment.features.enableMockData) {
      this.logger.info('Mock: Password reset confirmed');
      return of(void 0).pipe(delay(1000));
    }

    return this.http.post<void>(
      `${this.baseUrl}${API_ENDPOINTS.AUTH.RESET_PASSWORD_CONFIRM}`,
      request
    );
  }

  /**
   * Sign in with Google
   */
  signInWithGoogle(): Observable<AuthData> {
    if (environment.features.enableMockData) {
      this.logger.info('Mock: Google sign in initiated');
      // Return mock auth data
      const mockAuthData: AuthData = {
        user: {
          _id: '1',
          email: 'google.user@example.com',
          fullName: 'Google User',
          role: 'merchant',
          country: 'GE',
          createdAt: new Date(),
          updatedAt: new Date(),
          emailVerified: true,
          phoneVerified: false,
          twoFactorEnabled: false,
          preferences: {
            language: 'en',
            theme: 'light',
            currency: 'GEL',
            timezone: 'Asia/Tbilisi',
            notifications: {
              email: true,
              sms: false,
              push: true,
              transactionAlerts: true,
              securityAlerts: true,
              marketingEmails: false,
            },
          },
        },
        tokens: {
          accessToken: 'mock-google-access-token',
          refreshToken: 'mock-google-refresh-token',
          expiresIn: 0, // Tokens don't expire
          tokenType: 'Bearer',
        },
      };
      
      return of(mockAuthData).pipe(delay(1000));
    }

    // Real API implementation would handle OAuth flow
    return this.http.post<LoginResponse>(
      `${this.baseUrl}${API_ENDPOINTS.AUTH.GOOGLE_AUTH}`,
      {}
    ).pipe(
      switchMap((response) => {
        if (!response.success || !response.data?.token) {
          throw new Error(response.message || 'Google sign in failed');
        }

        const token = response.data.token;
        
        // Store token so interceptor can use it
        sessionStorage.setItem('token', token);

        // Fetch user data after Google login
        return this.loadCurrentUser(token).pipe(
          map((user) => ({
            user,
            tokens: {
              accessToken: token,
              tokenType: 'Bearer' as const,
              expiresIn: 0, // Tokens don't expire
            },
          }))
        );
      }),
      catchError((error) => {
        this.logger.error('Google sign in error', error);
        const errorMessage = error.error?.message || error.message || 'Google sign in failed';
        return throwError(() => new Error(errorMessage));
      })
    );
  }
}
